"use client";
import { getApiBase } from "@/configs";

import PublisherConfig from "@/configs/publisher.json";
import { isPaidAccessActive } from "@/lib/article-access";
import { getAuthToken, handleAuthError, hasAuthToken } from "@/lib/auth-cookie";
import axios from "axios";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

const API_BASE = getApiBase();
export const SUBSCRIPTION_CHANGED_EVENT = "subscription-changed";

export function postUnsubscribe(token: string) {
  return axios.post(`${API_BASE}/public/unsubscribe`, { token });
}

export interface CurrentSitePayment {
  type?: string;
  endedAt?: number;
  canceledAt?: number;
}

interface SubscriptionContextValue {
  isSignedIn: boolean;
  isSubscribed: boolean;
  isSiteOwner: boolean;
  isPaidSubscriber: boolean;
  currentPayment: CurrentSitePayment | null;
  isLoading: boolean;
  isUnsubscribing: boolean;
  unsubscribeError: string | null;
  refresh: () => Promise<void>;
  unsubscribe: () => Promise<boolean>;
  clearUnsubscribeError: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

function findCurrentSubscription(subscriptions: Array<any> | undefined): any | null {
  if (!Array.isArray(subscriptions)) return null;
  return subscriptions.find((item) => item?.publisher === PublisherConfig.username) || null;
}

export function SubscriptionProvider({
  authVersion = 0,
  children,
}: {
  authVersion?: number;
  children: ReactNode;
}) {
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isSiteOwner, setIsSiteOwner] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [currentPayment, setCurrentPayment] = useState<CurrentSitePayment | null>(null);
  const [unsubscribeToken, setUnsubscribeToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUnsubscribing, setIsUnsubscribing] = useState(false);
  const [unsubscribeError, setUnsubscribeError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setIsSignedIn(false);
      setIsSiteOwner(false);
      setIsSubscribed(false);
      setCurrentPayment(null);
      setUnsubscribeToken(null);
      setIsLoading(false);
      return;
    }

    setIsSignedIn(true);
    setIsLoading(true);
    setUnsubscribeError(null);

    try {
      const response = await axios.get(`${API_BASE}/account/login/info`, {
        headers: { Authorization: token },
      });
      const publishers = response.data?.data?.account?.publishers;
      setIsSiteOwner(Array.isArray(publishers) && publishers.some((item: any) => item?.username === PublisherConfig.username));
      const subscriptions = response.data?.data?.account?.subscriptions;
      const match = findCurrentSubscription(subscriptions);
      if (match) {
        setIsSubscribed(true);
        setUnsubscribeToken(typeof match.unsubscribeToken === "string" && match.unsubscribeToken ? match.unsubscribeToken : null);
        const payment = match.payment;
        setCurrentPayment(
          payment && typeof payment === "object"
            ? {
                type: payment.type,
                endedAt: typeof payment.endedAt === "number" ? payment.endedAt : undefined,
                canceledAt: typeof payment.canceledAt === "number" ? payment.canceledAt : undefined,
              }
            : null,
        );
      } else {
        setIsSubscribed(false);
        setCurrentPayment(null);
        setUnsubscribeToken(null);
      }
    } catch (err) {
      if (handleAuthError(err) || !hasAuthToken()) {
        setIsSignedIn(false);
        setIsSiteOwner(false);
        setIsSubscribed(false);
        setCurrentPayment(null);
        setUnsubscribeToken(null);
      } else {
        setIsSubscribed(false);
        setCurrentPayment(null);
        setUnsubscribeToken(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    if (!unsubscribeToken) {
      setUnsubscribeError("No unsubscribe token found.");
      return false;
    }

    setIsUnsubscribing(true);
    setUnsubscribeError(null);
    try {
      await postUnsubscribe(unsubscribeToken);
      if (currentPayment) {
        setCurrentPayment((payment) => (payment ? { ...payment, canceledAt: Math.floor(Date.now() / 1000) } : payment));
      } else {
        setIsSubscribed(false);
        setCurrentPayment(null);
        setUnsubscribeToken(null);
      }
      return true;
    } catch (err: any) {
      const serverMessage = err.response?.data?.error || err.response?.data?.message || err.message;
      setUnsubscribeError(serverMessage || "Failed to unsubscribe. Please try again.");
      return false;
    } finally {
      setIsUnsubscribing(false);
    }
  }, [currentPayment, unsubscribeToken]);

  useEffect(() => {
    refresh();
  }, [authVersion, refresh]);

  useEffect(() => {
    const handleChanged = () => {
      refresh();
    };
    window.addEventListener(SUBSCRIPTION_CHANGED_EVENT, handleChanged);
    return () => {
      window.removeEventListener(SUBSCRIPTION_CHANGED_EVENT, handleChanged);
    };
  }, [refresh]);

  const isPaidSubscriber = isPaidAccessActive(currentPayment);

  const value = useMemo<SubscriptionContextValue>(
    () => ({
      isSignedIn,
      isSubscribed,
      isSiteOwner,
      isPaidSubscriber,
      currentPayment,
      isLoading,
      isUnsubscribing,
      unsubscribeError,
      refresh,
      unsubscribe,
      clearUnsubscribeError: () => setUnsubscribeError(null),
    }),
    [
      isSignedIn,
      isSubscribed,
      isSiteOwner,
      isPaidSubscriber,
      currentPayment,
      isLoading,
      isUnsubscribing,
      unsubscribeError,
      refresh,
      unsubscribe,
    ],
  );

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}

export function useSubscription(): SubscriptionContextValue {
  const value = useContext(SubscriptionContext);
  if (!value) {
    return {
      isSignedIn: false,
      isSubscribed: false,
      isSiteOwner: false,
      isPaidSubscriber: false,
      currentPayment: null,
      isLoading: false,
      isUnsubscribing: false,
      unsubscribeError: null,
      refresh: async () => {},
      unsubscribe: async () => false,
      clearUnsubscribeError: () => {},
    };
  }
  return value;
}

export function notifySubscriptionChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(SUBSCRIPTION_CHANGED_EVENT));
}
